import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { HTTP_STATUS } from 'common-react/constants/http-status';

export const redirectToCipm4Login = (
  invitation: string | undefined,
  codeChallenge: string,
  passthroughParams?: Record<string, string>,
) => {
  const url = new URL(`${import.meta.env.VITE_CIPM4_URL}/authorize`);
  const params = url.searchParams;

  // forward CIPM4-supplied params (e.g. ext-prompt) first so our own values below always take precedence
  for (const [key, value] of Object.entries(passthroughParams ?? {})) {
    params.set(key, value);
  }

  params.set('redirect_uri', import.meta.env.VITE_CIPM4_REDIRECT_URI);
  params.set('client_id', import.meta.env.VITE_CIPM4_CLIENT_ID);
  if (invitation) {
    params.set('invitation', invitation);
  }
  params.set('organization', import.meta.env.VITE_CIPM4_ORGANIZATION);
  params.set('organization_name', import.meta.env.VITE_CIPM4_ORGANIZATION_NAME);
  params.set('response_type', import.meta.env.VITE_CIPM4_RESPONSE_TYPE);
  params.set('response_mode', import.meta.env.VITE_CIPM4_RESPONSE_MODE);
  params.set('state', import.meta.env.VITE_CIPM4_STATE);
  params.set('scope', import.meta.env.VITE_CIPM4_SCOPE);
  params.set('nonce', import.meta.env.VITE_CIPM4_NONCE);
  params.set('ui_locales', import.meta.env.VITE_CIPM4_UI_LOCALES);
  params.set('code_challenge', codeChallenge);
  params.set('code_challenge_method', 'S256');

  window.location.href = url.toString();
};

export const redirectToCipm4Logout = () => {
  window.location.href = `${
    import.meta.env.VITE_CIPM4_URL
  }/v2/logout?client_id=${
    import.meta.env.VITE_CIPM4_CLIENT_ID
  }&returnTo=${encodeURIComponent(import.meta.env.VITE_CIPM4_REDIRECT_URI)}`;
};

export const redirectToCipm4Profile = (onOpenPersonalDetails: () => void) => {
  onOpenPersonalDetails();

  const backRedirectionState = encodeURIComponent(
    new URL(document.location.toString()).pathname.replace('/', ''),
  );

  const backRedirectionUri = `${import.meta.env.VITE_CIPM4_REDIRECT_URI}`;

  const url = new URL(`${import.meta.env.VITE_CIPM4_URL}/authorize`);
  const params = url.searchParams;

  params.set('ext-manage-profile', 'true');
  params.set('client_id', import.meta.env.VITE_CIPM4_CLIENT_ID);
  params.set('redirect_uri', backRedirectionUri);
  params.set('response_type', import.meta.env.VITE_CIPM4_RESPONSE_TYPE);
  params.set('state', backRedirectionState);
  params.set('scope', import.meta.env.VITE_CIPM4_SCOPE);
  params.set('organization', import.meta.env.VITE_CIPM4_ORGANIZATION);

  window.location.href = url.toString();
};

export const redirectToCipm4ChangePassword = (
  onOpenChangePassword: () => void,
) => {
  onOpenChangePassword();

  const backRedirectionState = encodeURIComponent(
    new URL(document.location.toString()).pathname.replace('/', ''),
  );

  const backRedirectionUri = `${import.meta.env.VITE_CIPM4_REDIRECT_URI}`;

  const url = new URL(`${import.meta.env.VITE_CIPM4_URL}/authorize`);
  const params = url.searchParams;

  params.set('ext-manage-profile', 'true');
  params.set('ext-target-form', 'changePassword');
  params.set('client_id', import.meta.env.VITE_CIPM4_CLIENT_ID);
  params.set('redirect_uri', backRedirectionUri);
  params.set('response_type', import.meta.env.VITE_CIPM4_RESPONSE_TYPE);
  params.set('state', backRedirectionState);
  params.set('scope', import.meta.env.VITE_CIPM4_SCOPE);
  params.set('organization', import.meta.env.VITE_CIPM4_ORGANIZATION);
  params.set('ui_locales', import.meta.env.VITE_CIPM4_UI_LOCALES);

  window.location.href = url.toString();
};

export const getCipm4SchemeAuthenticationJwtTokenUsingAuthorizationCode =
  async (authorizationCode: string, codeVerifier: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PLUTO_API_ENDPOINT}/authentication/token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            scheme: AuthenticationScheme.CIPM4,
            'auth-code': authorizationCode,
            'code-verifier': codeVerifier,
          }),
        },
      );

      if (response.status === HTTP_STATUS.UNAUTHORIZED) {
        return;
      }

      if (response.ok) {
        return await response.text();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      return;
    }
  };

export const getCipm4SchemeAuthenticationJwtTokenUsingRefreshToken = async (
  refreshToken: string,
) => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_PLUTO_API_ENDPOINT}/authentication/token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheme: AuthenticationScheme.CIPM4,
          'refresh-token': refreshToken,
        }),
      },
    );
    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      return;
    }

    if (response.ok) {
      return await response.text();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    return;
  }
};
